import { humanize } from './format';

/**
 * The people vocabularies: employment types and separation reasons.
 *
 * Each list is the server's enum, in the server's spelling, with the words a
 * person reads beside it. There is one copy. The requisition form, the
 * add-employee wizard, the candidate conversion drawer and the employee panel
 * used to carry their own — "Fixed term (contractual)" in one, "Contractual
 * (fixed term)" and no part-time or intern option in another — so the same
 * hire read differently depending on the door it came through.
 */

/** The server's `EmploymentType` enum, labelled. Order is the order offered. */
export const EMPLOYMENT_TYPE_OPTIONS = [
  { value: 'probationary', label: 'Probationary' },
  { value: 'regular', label: 'Regular' },
  { value: 'fixed_term', label: 'Fixed term (contractual)' },
  { value: 'project_based', label: 'Project based' },
  { value: 'part_time', label: 'Part time' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'intern', label: 'Intern' },
] as const;

export type EmploymentTypeValue = (typeof EMPLOYMENT_TYPE_OPTIONS)[number]['value'];

export const EMPLOYMENT_TYPE_VALUES = EMPLOYMENT_TYPE_OPTIONS.map((option) => option.value) as [
  EmploymentTypeValue,
  ...EmploymentTypeValue[],
];

/**
 * An employment type in words. The prototype's "contractual" is the server's
 * `fixed_term`, and reads the same; anything unknown is humanized, not blank.
 */
export function employmentTypeLabel(value: string | null | undefined): string {
  if (!value) return '—';
  const code = value === 'contractual' ? 'fixed_term' : value;
  return EMPLOYMENT_TYPE_OPTIONS.find((option) => option.value === code)?.label ?? humanize(value);
}

/** The fixed separation reasons, as the server's create route accepts them. */
export const OFFBOARDING_REASON_OPTIONS = [
  { value: 'resignation', label: 'Resignation' },
  { value: 'end_of_contract', label: 'End of contract' },
  { value: 'termination', label: 'Termination' },
  { value: 'retirement', label: 'Retirement' },
  { value: 'redundancy', label: 'Redundancy' },
] as const;

/** A separation reason in words; an unrecognised one is humanized rather than refused. */
export function offboardingReasonLabel(value: string | null | undefined): string {
  if (!value) return '—';
  const code = value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  return (
    OFFBOARDING_REASON_OPTIONS.find((option) => option.value === code)?.label ?? humanize(value)
  );
}
